import { isEmpty } from 'lodash-es';

import Notification from '../../components/Notification/Notification';
import responseCodes from '../../constants/responseCodes';
import i18n from '../../i18n';
import * as userService from '../../services/userService';
import globalActionConstants from '../constants/globalActionConstants';
import userActionConstants from '../constants/userActionConstants';

const t = (key) => i18n.t(key, { ns: 'common' });

export const updateProfilePicture =
  (file, type, userId, successCallback = null) =>
  async (dispatch) => {
    dispatch({ type: globalActionConstants.SET_LOADING_ON });
    try {
      const response = await userService.uploadProfileImage(file, type, userId);
      dispatch({ type: globalActionConstants.SET_LOADING_OFF });
      if (response.data.response_code === responseCodes.OK) {
        dispatch({ type: userActionConstants.UPDATE_USER_PROFILE, payload: response.data.payload });
        successCallback?.();
        Notification.success(t('notifications.imageUpdated'));
      } else {
        Notification.error(response.data.error);
      }
    } catch (err) {
      dispatch({ type: globalActionConstants.SET_LOADING_OFF });
      Notification.error(t('notifications.somethingWentWrong'));
    }
  };

export const deleteCoverPicture = (imageId) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });
  try {
    const response = await userService.deleteCoverImage(imageId);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.DELETE_COVER_IMAGE });
      Notification.success(t('notifications.imageDeleted'));
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const updateUserProfile = (userId, data, successCallback, errorCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });
  try {
    const response = await userService.updateUserProfile(userId, data);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.UPDATE_USER_PROFILE, payload: response.data.payload });
      successCallback?.(data.personal_url);
      Notification.success(t('notifications.profileUpdated'));
    } else {
      if (response.data.payload.errors[0] === 'Personal url has already been taken') {
        return errorCallback?.();
      }
      Notification.error(response.data.payload.errors[0]);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const updatePassword = (userId, oldPassword, newPassword) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.updatePassword(userId, oldPassword, newPassword);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      Notification.success(t('notifications.passwordChanged'));
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const getUserDetails = (userId) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });
  try {
    const response = await userService.getUserDetails(userId);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.UPDATE_USER_PROFILE, payload: response.data.payload });
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const getURLProfileDetails = (url, errorCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });
  try {
    const response = await userService.getURLProfileDetails(url);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.GET_URL_PROFILE_DETAILS, payload: response.data.payload });
    } else {
      dispatch({ type: userActionConstants.GET_URL_PROFILE_DETAILS, payload: { user: {} } });
      errorCallback?.();
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: userActionConstants.GET_URL_PROFILE_DETAILS, payload: { user: {} } });
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    errorCallback?.();
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const fetchLinkedinProfile = (url, successCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });
  try {
    const response = await userService.fetchLinkedinProfile(url);
    const linkedinData = response.data.payload.linkedin_data;
    if (response.data.response_code === responseCodes.OK && !isEmpty(linkedinData)) {
      successCallback(linkedinData);
    } else {
      Notification.error(t('notifications.linkedinImportFailed'));
    }
  } catch (err) {
    Notification.error(t('notifications.somethingWentWrong'));
  } finally {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
  }
};

export const closeAccount = (userId, userPassword, closureReasons, otherReason) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.closeAccount(userId, userPassword, closureReasons, otherReason);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });

    if (response.data.response_code === responseCodes.OK) {
      Notification.success(t('notifications.accountClosed'));
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const getEmails = (userId) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.getEmails(userId);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });

    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.GET_EMAILS, payload: response.data.payload });
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const createEmail = (userId, email, successCallback, errorCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.createEmail(userId, email);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.CREATE_EMAIL, payload: response.data.payload });
      successCallback?.();
      Notification.success(t('notifications.emailAdded'));
    } else {
      if (response.data.payload.errors.length > 0) {
        return errorCallback?.(response.data.payload.errors[0]);
      }
      Notification.error(response.data.payload.errors[0]);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const deleteEmail = (emailId, successCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.deleteEmail(emailId);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });

    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.DELETE_EMAIL, payload: emailId });
      successCallback();
      Notification.success(t('notifications.emailDeleted'));
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const updatePrimaryEmail = (id, password, userId, successCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.updatePrimaryEmail(id, password, userId);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });

    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.UPDATE_PRIMARY_EMAIL, payload: id });
      successCallback();
      Notification.success(t('notifications.primaryEmailUpdated'));
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const verifyAdditonalEmail = (email, userId) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.verifyAdditonalEmail(email, userId);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });

    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.VERIFY_EMAIL, payload: email });
      Notification.success(t('notifications.emailVerified'));
    } else {
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const getExperientialRewardDetails = () => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.getExperientialRewardDetails();
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.GET_EXP_REWARD_DETAILS, payload: response.data.payload });
    } else {
      dispatch({ type: globalActionConstants.SET_LOADING_OFF });
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};

export const updateExperientialRewardDetails = (address, successCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.updateExperientialRewardDetails(address);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.CREATE_EXP_REWARD_DETAILS });

      if (successCallback) successCallback();
      Notification.success(t('notifications.addressUpdated'));
    } else {
      dispatch({ type: globalActionConstants.SET_LOADING_OFF });
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};
export const createExperientialRewardDetails = (address, successCallback) => async (dispatch) => {
  dispatch({ type: globalActionConstants.SET_LOADING_ON });

  try {
    const response = await userService.postExperientialRewardDetails(address);
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    if (response.data.response_code === responseCodes.OK) {
      dispatch({ type: userActionConstants.CREATE_EXP_REWARD_DETAILS });

      if (successCallback) successCallback();
      Notification.success(t('notifications.addressAdded'));
    } else {
      dispatch({ type: globalActionConstants.SET_LOADING_OFF });
      Notification.error(response.data.error);
    }
  } catch (err) {
    dispatch({ type: globalActionConstants.SET_LOADING_OFF });
    Notification.error(t('notifications.somethingWentWrong'));
  }
};
